/**
 * German labels of the Leads Cockpit (an internal tool, texts are written out like in the other
 * platform-admin panels). The codes behind them are the contract's: tri-server
 * `docs/onboarding-leads.md`.
 */
import type { AnalysisState, AssetClass, DataSlot, LeadStatus, ProvisioningState } from "../../OnboardingAPI";

/** The chips of the filter. `report_ready` is gone for new leads (no result mail any more); an older lead keeps it as a label only. */
export const STATUS_ORDER: LeadStatus[] = ["invited", "in_progress", "submitted", "analyzing"];

export const STATUS_LABEL: Record<LeadStatus, string> = {
  invited: "Eingeladen",
  in_progress: "In Bearbeitung",
  submitted: "Neu eingegangen",
  analyzing: "In Prüfung",
  report_ready: "Ergebnis gesendet",
};

export const STATUS_VARIANT: Record<LeadStatus, "outline" | "secondary" | "warning" | "default" | "success"> = {
  invited: "outline",
  in_progress: "secondary",
  submitted: "warning",
  analyzing: "default",
  report_ready: "success",
};

/** The tenant column of the list: short, and what to look at. */
export const PROVISIONING_LABEL: Record<ProvisioningState, string> = {
  pending: "Ausstehend",
  done: "Angelegt",
  existing_user: "Konflikt",
  failed: "Fehler",
};

export const PROVISIONING_VARIANT: Record<ProvisioningState, "outline" | "secondary" | "warning" | "default" | "success" | "destructive"> = {
  pending: "outline",
  done: "success",
  existing_user: "warning",
  failed: "destructive",
};

export const ANALYSIS_LABEL: Record<AnalysisState, string> = {
  idle: "Noch nicht gestartet",
  running: "Läuft",
  done: "Fertig",
  failed: "Fehlgeschlagen",
};

export const ASSET_CLASS_LABEL: Record<AssetClass, string> = {
  residential: "Wohnen",
  office: "Büro",
  retail: "Handel",
  industrial: "Industrie und Logistik",
  datacenter: "Rechenzentrum",
  hotel: "Hotel",
  other: "Sonstiges",
};

export const SLOT_LABEL: Record<DataSlot, string> = {
  buildings: "Gebäude",
  assets: "Anlagen",
  spaces: "Räume und Geschosse",
  floorplans: "Grundrisse",
  assetDetails: "Anlagendetails",
  maintenance: "Wartung und Prüfungen",
  responsibilities: "Zuständigkeiten und Zustand",
  catalogs: "Kataloge und Schlüsseltabellen",
  escapePlans: "Flucht- und Rettungspläne",
  other: "Weiteres",
  logo: "Logo",
};

/** A slot code the cockpit does not know is shown as it is, never hidden. */
export const slotLabel = (slot: string): string => (SLOT_LABEL as Record<string, string>)[slot] ?? slot;

export const assetClassLabel = (value: string | undefined): string =>
  value ? ((ASSET_CLASS_LABEL as Record<string, string>)[value] ?? value) : "-";

const dateTime = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });
const dateOnly = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" });

const parse = (value: string | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value: string | undefined): string => {
  const date = parse(value);
  return date ? dateTime.format(date) : "-";
};

export const formatDate = (value: string | undefined): string => {
  const date = parse(value);
  return date ? dateOnly.format(date) : "-";
};

export const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace(".", ",")} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

/** A share (0..1) as a percentage. */
export const formatShare = (share: number): string => `${Math.round(share * 100)} %`;

/** The server's step slugs (the page's own steps) in the cockpit's words. An unknown slug is shown as it is. */
export const STEP_LABEL: Record<string, string> = {
  welcome: "Willkommen",
  company: "Unternehmen",
  properties: "Immobilien",
  buildings: "Gebäude",
  assets: "Anlagen",
  spaces: "Räume und Geschosse",
  floorplans: "Grundrisse",
  escape: "Flucht- und Rettungspläne",
  more: "Weitere Unterlagen",
  questions: "Fragen",
  review: "Prüfen",
  done: "Abgeschlossen",
};
/** A step the page names as the cockpit does; an unknown slug (a newer page) becomes readable words instead of a code. */
export const stepLabel = (slug: string | undefined): string => {
  if (!slug) return "-";
  const known = STEP_LABEL[slug];
  if (known) return known;
  const words = slug.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[-_]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "-";
};

/** "vor 5 Min.", "vor 2 Std.", "vor 3 Tagen"; "-" without a time. */
export function relativeTime(value: string | undefined, now: number = Date.now()): string {
  const date = parse(value);
  if (!date) return "-";
  const minutes = Math.max(0, Math.round((now - date.getTime()) / 60000));
  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.round(hours / 24);
  return `vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
}
